import axios from 'axios';
import { API_CONFIG } from '../config';
import { getParachuteAuthToken } from '../utils/parachuteAuthToken';

/** Product role: Admin vs Client. */
export type UserRole = 'admin' | 'client';

/** Login kind: platform root, law firm, or end-user. */
export type LoginType = 'Root' | 'Firm' | 'Client';

export interface LoginResponse {
  data: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    isActive: boolean;
    /** Canonical: Root | Firm | Client (set by client from JWT). */
    loginType: LoginType;
    /** JWT role claim (Admin, Client, …). Firm workspace uses Role Admin + login_type claim firm. */
    role: string;
    token?: string;
    /** Linked company name, or law firm name (from login API). */
    companyName?: string;
  };
}

interface ApiAuthResponse {
  token: string;
  email: string;
  displayName?: string;
  expiresAt: string;
  /** Linked company name, or law firm name when no company row (from login API). */
  companyName?: string;
}

/** Claim keys used by ASP.NET Core JWT and common alternatives; value may be string or string[]. */
const JWT_ROLE_CLAIM_KEYS = ['http://schemas.microsoft.com/ws/2008/06/identity/claims/role', 'role', 'Role'] as const;

const JWT_LOGIN_TYPE_CLAIM_KEYS = ['login_type', 'loginType'] as const;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Firm workspace tenant id from JWT (`law_firm_id`), same claim the API uses for
 * `/api/pct/v1/lawfirms/{guid}/…` authorization.
 */
export function getLawFirmIdFromJwt(token: string | null | undefined): string | null {
  if (!token || typeof token !== 'string') return null;
  const p = decodeJwtPayload(token);
  if (!p) return null;
  const v = p.law_firm_id;
  if (typeof v === 'string' && v.trim() !== '') return v.trim();
  return null;
}

function readJwtRoleClaim(payload: Record<string, unknown>): string {
  for (const key of JWT_ROLE_CLAIM_KEYS) {
    const v = payload[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (Array.isArray(v) && v.length > 0) {
      const first = v[0];
      if (typeof first === 'string' && first.trim()) return first.trim();
    }
  }
  return 'Client';
}

function readJwtLoginTypeClaim(payload: Record<string, unknown>): string | null {
  for (const key of JWT_LOGIN_TYPE_CLAIM_KEYS) {
    const v = payload[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/** Map API login_type claim (Root, firm, client) to app LoginType. */
export function jwtLoginTypeClaimToLoginType(loginTypeClaim: string): LoginType {
  const s = (loginTypeClaim ?? '').trim();
  if (s === 'Root') return 'Root';
  if (s.toLowerCase() === 'firm') return 'Firm';
  if (s.toLowerCase() === 'client') return 'Client';
  return 'Client';
}

function parseJwtRole(token?: string): string {
  if (!token) return 'Client';
  const payload = decodeJwtPayload(token);
  if (!payload) return 'Client';
  return readJwtRoleClaim(payload);
}

/** Fallback when login_type claim is absent: legacy FirmAdmin, or role-only heuristics. */
export function jwtRoleToLoginType(jwtRole: string): LoginType {
  const r = (jwtRole ?? '').trim();
  if (r === 'FirmAdmin') return 'Firm';
  if (r === 'Staff') return 'Firm';
  if (r === 'Admin' || r === 'admin') return 'Root';
  return 'Client';
}

/** Single mapping: LoginType → UserRole (Client → client; Root/Firm → admin). */
export function loginTypeToUserRole(loginType: LoginType): UserRole {
  return loginType === 'Client' ? 'client' : 'admin';
}

/**
 * Derive UserRole and LoginType from login response.
 * After `login()`, `loginType` is always set; `role` holds the raw JWT claim for fallback/tests.
 */
export function parseLoginIdentity(data: LoginResponse['data']): {
  role: UserRole;
  loginType: LoginType;
} {
  const lt = data.loginType;
  if (lt === 'Root' || lt === 'Firm' || lt === 'Client') {
    return { loginType: lt, role: loginTypeToUserRole(lt) };
  }
  const loginType = jwtRoleToLoginType((data.role ?? '').trim());
  return { loginType, role: loginTypeToUserRole(loginType) };
}

async function login(username: string, password: string): Promise<LoginResponse> {
  try {
    const response = await axios.post<ApiAuthResponse>(
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTHENTICATE}`,
      { email: username, password },
      {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.data) {
      throw new Error('No data received from authentication API');
    }

    const apiData = response.data;
    const displayName = (apiData.displayName ?? '').trim();
    const parts = displayName.split(/\s+/).filter(Boolean);
    const firstName = parts[0] ?? '';
    const lastName = parts.slice(1).join(' ');
    const jwtRole = parseJwtRole(apiData.token);
    const payload = decodeJwtPayload(apiData.token);
    const loginTypeClaim = payload ? readJwtLoginTypeClaim(payload) : null;
    const loginType = loginTypeClaim ? jwtLoginTypeClaimToLoginType(loginTypeClaim) : jwtRoleToLoginType(jwtRole);
    const companyNameRaw = apiData.companyName;
    const companyName =
      typeof companyNameRaw === 'string' && companyNameRaw.trim() !== '' ? companyNameRaw.trim() : undefined;

    return {
      data: {
        id: apiData.email,
        firstName,
        lastName,
        email: apiData.email,
        phone: '',
        isActive: true,
        loginType,
        role: jwtRole,
        token: apiData.token,
        companyName,
      },
    };
  } catch (error: unknown) {
    console.error('Login error:', error);
    const ax = error as { response?: { data?: { message?: string } } };
    console.error('Error response:', ax.response?.data);
    const msg = ax.response?.data?.message;
    throw new Error(typeof msg === 'string' && msg ? msg : 'Login failed');
  }
}

async function completePasswordReset(token: string, newPassword: string): Promise<void> {
  try {
    await axios.post(
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH_PASSWORD_RESET_COMPLETE}`,
      { token, newPassword },
      {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (error: unknown) {
    const ax = error as { response?: { data?: { message?: string } } };
    const msg = ax.response?.data?.message;
    throw new Error(typeof msg === 'string' && msg ? msg : 'Could not reset password.');
  }
}

async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const token = getParachuteAuthToken();
  if (!token) {
    throw new Error('You must be signed in to change your password.');
  }
  try {
    await axios.post(
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH_CHANGE_PASSWORD}`,
      { currentPassword, newPassword },
      {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  } catch (error: unknown) {
    const ax = error as { response?: { data?: { message?: string } } };
    const msg = ax.response?.data?.message;
    throw new Error(typeof msg === 'string' && msg ? msg : 'Could not update password.');
  }
}

const LoginApi = { login, changePassword, completePasswordReset };

export default LoginApi;
